import { ShipmentScanEventType } from './../types/main/ShipmentType'
import { DispatchType, DispatchItemType } from './../types/main/DispatchType'

const str = (v: unknown, fallback = ''): string =>
	v === null || v === undefined ? fallback : String(v)
const num = (v: unknown, fallback = 0): number =>
	v === null || v === undefined || v === '' ? fallback : Number(v)

/**
 * 배송 건 한 줄 (DISPATCH_PLAN.md §3). 서버(`Dispatch.schema.ts`, API)가 이미 카멜케이스로 준다 —
 * 여기서는 null 을 빈 값으로 고르고 상태를 다시 파생한다(송장 없음 → preparing, 있음 → inTransit, 완료일 → completed).
 */
class Dispatch implements DispatchType {
	constructor(raw: Partial<DispatchType> & { [key: string]: unknown } = {}) {
		this.id = str(raw.id)
		this.kind = str(raw.kind, 'sale')
		this.direction = str(raw.direction, 'out')
		this.orderId = str(raw.orderId)
		this.boxNo = num(raw.boxNo, 1)
		this.boxCount = num(raw.boxCount, 1)
		this.customerId = str(raw.customerId)
		this.partyName = str(raw.partyName)
		this.orderedAt = str(raw.orderedAt)
		this.shippedAt = str(raw.shippedAt)
		this.deliveredAt = str(raw.deliveredAt)
		this.countryIso = str(raw.countryIso)
		this.carrier = str(raw.carrier)
		this.trackingNumber = str(raw.trackingNumber)
		this.boxId = str(raw.boxId)
		this.boxCode = str(raw.boxCode)
		this.weightKg = num(raw.weightKg)
		this.receiverName = str(raw.receiverName)
		this.receiverPhone = str(raw.receiverPhone)
		this.address1 = str(raw.address1)
		this.address2 = str(raw.address2)
		this.city = str(raw.city)
		this.state = str(raw.state)
		this.postalCode = str(raw.postalCode)
		this.memo = str(raw.memo)
		this.updatedAt = str(raw.updatedAt)
		this.createdAt = str(raw.createdAt)
		this.createdBy = str(raw.createdBy)
		this.channel = str(raw.channel)
		this.seasonId = str(raw.seasonId)
		this.customerEmail = str(raw.customerEmail)
		this.currency = str(raw.currency)
		this.totalAmount =
			raw.totalAmount === null || raw.totalAmount === undefined
				? null
				: Number(raw.totalAmount)
		this.paidAt = str(raw.paidAt)
		this.paidYn = raw.paidYn === null || raw.paidYn === undefined ? 1 : Number(raw.paidYn)
		this.preparedAt = str(raw.preparedAt)
		this.refundedAt = str(raw.refundedAt)
		this.orderCountry = str(raw.orderCountry)
		this.itemSummary = str(raw.itemSummary)
		this.serials = str(raw.serials)
		this.itemCount = num(raw.itemCount)
		this.shipmentId = str(raw.shipmentId)
		this.serviceType = str(raw.serviceType)
		this.widthCm = num(raw.widthCm)
		this.heightCm = num(raw.heightCm)
		this.depthCm = num(raw.depthCm)
		this.labelMime = str(raw.labelMime)
		this.hasLabel = num(raw.hasLabel)
		this.lastStatusCode = str(raw.lastStatusCode)
		this.lastStatusDesc = str(raw.lastStatusDesc)
		this.lastTrackedAt = str(raw.lastTrackedAt)
		this.scanEvents = Array.isArray(raw.scanEvents) ? raw.scanEvents : []
		this.labelCreatedAt = str(raw.labelCreatedAt)
		this.status =
			this.deliveredAt !== ''
				? 'completed'
				: this.trackingNumber !== ''
				? 'inTransit'
				: 'preparing'
	}

	id: string
	kind: string
	direction: string
	orderId: string
	boxNo: number
	boxCount: number
	customerId: string
	partyName: string
	orderedAt: string
	shippedAt: string
	deliveredAt: string
	countryIso: string
	carrier: string
	trackingNumber: string
	boxId: string
	boxCode: string
	weightKg: number
	receiverName: string
	receiverPhone: string
	address1: string
	address2: string
	city: string
	state: string
	postalCode: string
	memo: string
	updatedAt: string
	createdAt: string
	createdBy: string
	channel: string
	seasonId: string
	customerEmail: string
	currency: string
	totalAmount: number | null
	paidAt: string
	/** 도매 주문의 입금 여부(1 · 0). 수동 건·소매는 1 */
	paidYn: number
	preparedAt: string
	refundedAt: string
	orderCountry: string
	itemSummary: string
	serials: string
	itemCount: number
	shipmentId: string
	serviceType: string
	widthCm: number
	heightCm: number
	depthCm: number
	labelMime: string
	hasLabel: number
	lastStatusCode: string
	lastStatusDesc: string
	lastTrackedAt: string
	scanEvents: ShipmentScanEventType[]
	labelCreatedAt: string
	status: string
}

class DispatchItem implements DispatchItemType {
	constructor(raw: Partial<DispatchItemType> = {}) {
		this.id = str(raw.id)
		this.dispatchId = str(raw.dispatchId)
		this.boxNo = num(raw.boxNo, 1)
		this.productId = str(raw.productId)
		this.productCode = str(raw.productCode)
		this.productName = str(raw.productName)
		this.freeName = str(raw.freeName)
		this.serial = str(raw.serial)
		this.sortNo = num(raw.sortNo)
		this.name = this.productName !== '' ? this.productName : this.freeName
	}

	id: string
	dispatchId: string
	boxNo: number
	productId: string
	productCode: string
	productName: string
	freeName: string
	serial: string
	sortNo: number
	name: string
}

export { Dispatch, DispatchItem }
